// ============================================
// 사용자 어휘 사전 (모호 표현 정의 + 처리 룰)
// LLM이 "낮은/높은/이탈" 등 모호 모디파이어 만나면 LOOKUP_TERM_DEFINITION 인텐션 호출 → 정의 응답 받아 정확한 후속 도구 호출
// ============================================

export const USER_TERM_DICTIONARY = {
  // 회원권 — 재등록률
  '재등록률': {
    meaning: '회원권 만료 후 재등록한 비율 (%)',
    domain: 'MEMBERSHIP',
    defaultAxis: 'membership',
    alternativeAxes: [],
    relatedIntention: 'CHECK_MEMBERSHIP+TOP+RE+REGISTRATION_STATISTICS_BY+PERIOD',
    metricField: 'reRegistrationRate',
    averageKey: 'totalReRegistrationRate',
    primaryMetricLabel: '재등록률',
    primaryMetricUnit: '%',
    modifiers: {
      '낮은': {
        operationalDef: '전체 평균(totalReRegistrationRate) 미만',
        suggestedAction: 'filter({field:"reRegistrationRate", condition:{type:"numeric_lte", value:<totalReRegistrationRate>}}) → sort({field:"reRegistrationRate", direction:"asc"})',
        businessContext: '이탈 위험 회원권 — 만료 후 재등록 안 할 가능성 높음. 보유 회원에게 retention 액션(연장 권유, SMS) 유효',
      },
      '높은': {
        operationalDef: '전체 평균(totalReRegistrationRate) 이상',
        suggestedAction: 'filter({field:"reRegistrationRate", condition:{type:"numeric_gte", value:<totalReRegistrationRate>}}) → sort({field:"reRegistrationRate", direction:"desc"})',
        businessContext: '잘 유지되는 회원권 — 충성도 높음',
      },
    },
    crossReferenceRule: {
      pattern: '해당/이/그/위/방금 본 회원권 + 보유 회원',
      action: 'call_api(CHECK_MEMBER+ALIVE_LIST, slot:"alive") → filter({field:"viewMembershipName", condition:{type:"exact", value:[buffer의 memberShipName 배열]}}) → show_display',
      targetCategory: 'MEMBER',
      targetIntention: 'CHECK_MEMBER+ALIVE_LIST',
      sourceField: 'memberShipName',
      targetField: 'viewMembershipName',
    },
  },
  // 회원 상태 — 홀딩 (카테고리 라우팅용. MEMBERSHIP 아닌 MEMBER 카테고리)
  '홀딩': {
    meaning: '회원권 일시정지 상태 회원 목록. MEMBER 카테고리 (MEMBERSHIP 아님).',
    domain: 'MEMBER',
    defaultAxis: null,
    alternativeAxes: [],
    relatedIntention: 'CHECK_MEMBER+HOLDING_LIST',
    metricField: null,
  },
  // 회원권 — 상위 매출 (카테고리 라우팅용. SALES 아닌 MEMBERSHIP 카테고리 — 회원권별 판매 순위)
  '상위 매출': {
    meaning: '회원권별 판매 순위 (판매 건수 기준 내림차순). MEMBERSHIP 카테고리.',
    domain: 'MEMBERSHIP',
    defaultAxis: null,
    alternativeAxes: [],
    relatedIntention: 'CHECK_MEMBERSHIP+TOP+SALES_STATISTICS_BY+PERIOD',
    metricField: 'salesCount',
  },
  // 회원권 — 출석률 (modifier-based filtering 전용. context별 의미 ambiguous하므로 primary metric 매핑 X — getPrimaryMetricForTerm은 null 반환)
  '출석률': {
    meaning: '맥락별 다른 metric. "전체 출석률"=activeRate(전체 회원 중 출석한 비율). 수업별/회원권별=row attendanceRate(예약 중 출석률). "평균 출석률"=avgAttendanceRate(출석 회원의 평균).',
    domain: 'MEMBERSHIP',
    defaultAxis: 'membership',
    alternativeAxes: ['lesson', 'coach', 'day'],
    relatedIntention: 'CHECK_MEMBERSHIP+TOP+ATTENDANCE_STATISTICS_BY+PERIOD',
    metricField: 'attendanceRate',
    // averageKey 의도적 부재 — call_api result message의 "(← 정답값, summary.X)" 라벨링 비활성. LLM이 backend summary 한국어 라벨로 직접 매칭.
    primaryMetricLabel: '출석률',
    primaryMetricUnit: '%',
    modifiers: {
      '낮은': {
        operationalDef: '회원권 row attendanceRate 기준 전체 평균 미만',
        suggestedAction: 'filter+sort asc',
        businessContext: '활용도 낮은 회원권 — 회원이 잘 안 다님',
      },
      '높은': {
        operationalDef: '회원권 row attendanceRate 기준 전체 평균 이상',
        suggestedAction: 'filter+sort desc',
        businessContext: '활용도 높은 회원권',
      },
    },
  },
};

// 사용자 input에서 어휘 + 모디파이어 탐지 패턴
export const TERM_PATTERNS = {
  '상위 매출': /상위\s*매출|매출\s*순위|인기\s*회원권|많이\s*팔린\s*회원권|회원권\s*판매\s*순위|회원권별\s*매출/,
  '재등록률': /재등록률|재등록\s*비율|재가입률|재등록\s*율/,
  '출석률': /출석률|출석\s*비율|출석\s*율/,
  '홀딩': /홀딩|일시\s*정지|홀드/,
};

export const MODIFIER_PATTERNS = {
  '낮은': /낮은|낮을수록|이탈|위험|꼴찌|하위|덜\s*유지/,
  '높은': /높은|높을수록|잘\s*유지|충성|상위|뛰어난|상위권/,
};

// axis 키워드 패턴 — input에 명시된 axis 추출 (USER TERM의 defaultAxis와 비교용)
export const AXIS_KEYWORD_PATTERNS = {
  lesson: /수업|레슨|클래스/,
  coach: /코치|강사|선생/,
  membership: /회원권|상품권/,
  day: /요일|day별/,
};

export function detectAxisFromInput(userInput) {
  if (!userInput) return null;
  for (const [axis, pattern] of Object.entries(AXIS_KEYWORD_PATTERNS)) {
    if (pattern.test(userInput)) return axis;
  }
  return null;
}

// 사용자 input에서 term만 탐지 (modifier 없어도 매칭). 메타지표 우선 metric 노출용
export function detectTermOnly(userInput) {
  if (!userInput) return null;
  for (const [term, pattern] of Object.entries(TERM_PATTERNS)) {
    if (pattern.test(userInput)) return term;
  }
  return null;
}

// term의 primary metric (백엔드 summary 필드 + 라벨 + 단위) 반환. 없으면 null
export function getPrimaryMetricForTerm(term) {
  const entry = USER_TERM_DICTIONARY[term];
  if (!entry || !entry.averageKey) return null;
  return {
    field: entry.averageKey,
    label: entry.primaryMetricLabel || term,
    unit: entry.primaryMetricUnit || '',
  };
}

// 사용자 input에서 (term, modifier) 탐지 — 매칭 안되면 null
export function detectTermAndModifier(userInput) {
  if (!userInput) return null;

  let detectedTerm = null;
  for (const [term, pattern] of Object.entries(TERM_PATTERNS)) {
    if (pattern.test(userInput)) { detectedTerm = term; break; }
  }
  if (!detectedTerm) return null;

  let detectedModifier = null;
  for (const [modifier, pattern] of Object.entries(MODIFIER_PATTERNS)) {
    if (pattern.test(userInput)) { detectedModifier = modifier; break; }
  }
  if (!detectedModifier) return null;

  return { term: detectedTerm, modifier: detectedModifier };
}

// 사전 lookup — term + modifier로 정의 + 룰 반환
// Multi-axis term (alternativeAxes.length > 0) — strip relatedIntention/domain to prevent
// LOOKUP-driven unit misroute. LLM must re-evaluate via get_intentions catalog using
// availableFields shape (see INTENTION_GUIDE [ENTITY UNIT RULE]).
export function lookupTermDefinition(term, modifier) {
  const entry = USER_TERM_DICTIONARY[term];
  if (!entry) return null;

  const modifierEntry = modifier ? entry.modifiers?.[modifier] : null;
  const isAxisAmbiguous = Array.isArray(entry.alternativeAxes) && entry.alternativeAxes.length > 0;

  return {
    term,
    meaning: entry.meaning,
    domain: isAxisAmbiguous ? null : entry.domain,
    relatedIntention: isAxisAmbiguous ? null : entry.relatedIntention,
    axisHint: isAxisAmbiguous
      ? `Multi-axis term (defaultAxis=${entry.defaultAxis}, alternatives=[${entry.alternativeAxes.join(',')}]). Do NOT use this LOOKUP result to pick a specific intention — re-evaluate via the current category's get_intentions list using availableFields signature (see INTENTION_GUIDE [ENTITY UNIT RULE]). Match user noun (회원/회원권/수업/etc.) to intention's items[] entity.`
      : null,
    metricField: entry.metricField,
    averageKey: entry.averageKey,
    modifier: modifier || null,
    operationalDef: modifierEntry?.operationalDef || null,
    suggestedAction: modifierEntry?.suggestedAction || null,
    businessContext: modifierEntry?.businessContext || null,
    crossReferenceRule: entry.crossReferenceRule || null,
  };
}

// 가짜 인텐션 메타 (모든 카테고리에 자동 노출)
export const LOOKUP_TERM_INTENTION = {
  intentionKey: 'LOOKUP_TERM_DEFINITION',
  description: '사용자 어휘 사전 조회. 모호 표현(낮은/높은/이탈/충성/위험 등) + 메트릭(재등록률/출석률 등) 의미와 처리 룰을 반환. 모호 input 받으면 다른 도구 호출 전에 먼저 호출 권장.',
  creative: false,
  params: [
    { name: 'term', type: 'string', required: true, description: '메트릭 어휘 (예: 재등록률, 출석률)' },
    { name: 'modifier', type: 'string', required: false, description: '모디파이어 (예: 낮은, 높은). 없으면 의미만 반환.' },
  ],
  availableFields: {
    term: '어휘',
    meaning: '의미',
    domain: '도메인',
    relatedIntention: '관련 인텐션',
    operationalDef: '실행 정의',
    suggestedAction: '추천 도구 호출',
    businessContext: '비즈니스 맥락',
    crossReferenceRule: 'cross-reference 룰',
  },
  endpoint: '/__client__/lookup_term',  // dummy
  service: '__client__',
};
